import { useState, useEffect, useRef } from 'react';
import {
  Image,
  RefreshCw,
  Search,
  ChevronLeft,
  ChevronRight,
  Trash2,
  AlertCircle,
  X,
  HardDrive,
  Hash,
  Download,
  ExternalLink,
  CheckSquare,
  Square,
  XCircle,
} from 'lucide-react';
import JSZip from 'jszip';
import {
  r2Gallery,
  R2Image,
  R2GalleryFilters,
  R2StorageStats,
} from '../../api/client';
import { formatDateTime, cn } from '../../lib/utils';

// --- PNG tEXt chunk embedding ---

function crc32(buf: Uint8Array): number {
  let crc = -1;
  for (let i = 0; i < buf.length; i++) {
    crc = (crc >>> 8) ^ crc32Table[(crc ^ buf[i]) & 0xff];
  }
  return (crc ^ -1) >>> 0;
}

const crc32Table = (() => {
  const table = new Uint32Array(256);
  for (let i = 0; i < 256; i++) {
    let c = i;
    for (let j = 0; j < 8; j++) {
      c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    }
    table[i] = c;
  }
  return table;
})();

/** Insert a PNG tEXt chunk (key="prompt") after the IHDR chunk. Returns original if not PNG. */
function embedPngTextChunk(pngBuffer: ArrayBuffer, prompt: string): ArrayBuffer {
  const src = new Uint8Array(pngBuffer);
  // Verify PNG signature
  const PNG_SIG = [137, 80, 78, 71, 13, 10, 26, 10];
  if (src.length < 8 || !PNG_SIG.every((b, i) => src[i] === b)) return pngBuffer;

  const encoder = new TextEncoder();
  const keyword = encoder.encode('prompt');
  const text = encoder.encode(prompt);
  // tEXt data = keyword + NUL + text
  const chunkData = new Uint8Array(keyword.length + 1 + text.length);
  chunkData.set(keyword, 0);
  chunkData[keyword.length] = 0; // NUL separator
  chunkData.set(text, keyword.length + 1);

  // Build chunk: 4-byte length + "tEXt" + data + 4-byte CRC
  const chunkType = encoder.encode('tEXt');
  const chunkLen = chunkData.length;
  const fullChunk = new Uint8Array(4 + 4 + chunkLen + 4);
  const view = new DataView(fullChunk.buffer);
  view.setUint32(0, chunkLen);
  fullChunk.set(chunkType, 4);
  fullChunk.set(chunkData, 8);
  // CRC covers type + data
  const crcInput = new Uint8Array(4 + chunkLen);
  crcInput.set(chunkType, 0);
  crcInput.set(chunkData, 4);
  view.setUint32(8 + chunkLen, crc32(crcInput));

  // Find end of IHDR chunk (8-byte sig + IHDR chunk)
  // IHDR chunk starts at offset 8: 4-byte length + 4 "IHDR" + data + 4 CRC
  const ihdrLen = new DataView(pngBuffer).getUint32(8);
  const insertAt = 8 + 4 + 4 + ihdrLen + 4; // after sig + IHDR

  const result = new Uint8Array(src.length + fullChunk.length);
  result.set(src.subarray(0, insertAt), 0);
  result.set(fullChunk, insertAt);
  result.set(src.subarray(insertAt), insertAt + fullChunk.length);
  return result.buffer;
}

/** Fetch images concurrently with a concurrency limit. Calls onProgress after each fetch. */
async function fetchImagesConcurrently(
  images: R2Image[],
  concurrency: number,
  onProgress: (current: number) => void,
  abortSignal?: AbortSignal,
): Promise<Map<string, ArrayBuffer>> {
  const results = new Map<string, ArrayBuffer>();
  let nextIndex = 0;
  let completed = 0;

  async function worker() {
    while (nextIndex < images.length) {
      if (abortSignal?.aborted) return;
      const idx = nextIndex++;
      const img = images[idx];
      try {
        const resp = await fetch(img.r2Url, { signal: abortSignal });
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        results.set(img.id, await resp.arrayBuffer());
      } catch (err) {
        if (abortSignal?.aborted) return;
        console.warn(`Failed to fetch ${img.id}:`, err);
      }
      completed++;
      onProgress(completed);
    }
  }

  const workers = Array.from({ length: Math.min(concurrency, images.length) }, () => worker());
  await Promise.all(workers);
  return results;
}

export default function R2Gallery() {
  const [images, setImages] = useState<R2Image[]>([]);
  const [stats, setStats] = useState<R2StorageStats | null>(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedImage, setSelectedImage] = useState<R2Image | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  // Selection state
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);

  // Download state
  const [downloading, setDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState({ current: 0, total: 0 });
  const downloadAbortRef = useRef<AbortController | null>(null);

  // Filters
  const [filters, setFilters] = useState<R2GalleryFilters>({
    limit: 24,
    offset: 0,
  });
  const [searchInput, setSearchInput] = useState('');

  const fetchData = async () => {
    try {
      setLoading(true);
      const [imagesRes, statsRes] = await Promise.all([
        r2Gallery.list(filters),
        r2Gallery.stats(),
      ]);
      setImages(imagesRes.images);
      setTotal(imagesRes.total);
      setStats(statsRes);
      setError(null);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to fetch images';
      console.error('Failed to fetch images:', error);
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [filters]);

  // Clear selection whenever the query changes (page, search, or device filter)
  // so "Delete Selected" can never target images outside the current result set.
  useEffect(() => {
    setSelectedIds(new Set());
  }, [filters]);

  // Clear selection when exiting select mode
  useEffect(() => {
    if (!selectMode) {
      setSelectedIds(new Set());
      // Abort any in-progress download
      downloadAbortRef.current?.abort();
      setDownloading(false);
    }
  }, [selectMode]);

  const handleSearch = () => {
    setFilters(prev => ({
      ...prev,
      search: searchInput || undefined,
      offset: 0,
    }));
  };

  const handlePageChange = (direction: 'prev' | 'next') => {
    const newOffset = direction === 'prev'
      ? Math.max(0, (filters.offset || 0) - (filters.limit || 24))
      : (filters.offset || 0) + (filters.limit || 24);
    setFilters(prev => ({ ...prev, offset: newOffset }));
  };

  const handleDelete = async (id: string, e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (!confirm('Are you sure you want to delete this image?')) return;

    try {
      setDeletingId(id);
      await r2Gallery.delete(id);
      // Refresh the list
      await fetchData();
      if (selectedImage?.id === id) {
        setSelectedImage(null);
      }
      // Remove from selection if selected
      setSelectedIds(prev => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to delete image';
      setError(message);
    } finally {
      setDeletingId(null);
    }
  };

  // Batch delete helper - deletes in chunks of 100
  const batchDelete = async (ids: string[]): Promise<{ deletedCount: number; errors: string[] }> => {
    const BATCH_SIZE = 100;
    let totalDeleted = 0;
    const allErrors: string[] = [];

    for (let i = 0; i < ids.length; i += BATCH_SIZE) {
      const batch = ids.slice(i, i + BATCH_SIZE);
      try {
        const result = await r2Gallery.bulkDelete(batch);
        totalDeleted += result.deletedCount;
        if (result.errors && result.errors.length > 0) {
          allErrors.push(...result.errors);
        }
      } catch (err) {
        allErrors.push(`Batch ${Math.floor(i / BATCH_SIZE) + 1} failed: ${err instanceof Error ? err.message : 'Unknown error'}`);
      }
    }

    return { deletedCount: totalDeleted, errors: allErrors };
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;

    const count = selectedIds.size;
    if (!confirm(`Are you sure you want to delete ${count} image${count > 1 ? 's' : ''}?`)) return;

    try {
      setBulkDeleting(true);
      const result = await batchDelete(Array.from(selectedIds));

      if (result.errors.length > 0) {
        setError(`Deleted ${result.deletedCount} of ${count} images. Errors: ${result.errors.slice(0, 5).join(', ')}${result.errors.length > 5 ? '...' : ''}`);
      }

      // Refresh and exit select mode
      await fetchData();
      setSelectedIds(new Set());
      setSelectMode(false);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to delete images';
      setError(message);
    } finally {
      setBulkDeleting(false);
    }
  };

  const handleDeleteAll = async () => {
    if (!confirm(`Are you sure you want to delete ALL ${total} images? This cannot be undone.`)) return;
    if (!confirm(`This will permanently delete ${total} images from R2 storage. Type "delete" in the next prompt to confirm.`)) return;

    const confirmation = prompt('Type "delete" to confirm deletion of all images:');
    if (confirmation?.toLowerCase() !== 'delete') {
      setError('Deletion cancelled - confirmation did not match');
      return;
    }

    try {
      setBulkDeleting(true);

      // Fetch all image IDs (in batches if needed)
      let allIds: string[] = [];
      let offset = 0;
      const batchSize = 500;

      while (true) {
        const response = await r2Gallery.list({ ...filters, limit: batchSize, offset });
        allIds = allIds.concat(response.images.map(img => img.id));
        if (response.images.length < batchSize || allIds.length >= response.total) {
          break;
        }
        offset += batchSize;
      }

      if (allIds.length === 0) {
        setError('No images to delete');
        return;
      }

      // Delete in batches
      const result = await batchDelete(allIds);

      if (result.errors.length > 0) {
        setError(`Deleted ${result.deletedCount} of ${allIds.length} images. Some errors occurred.`);
      }

      // Refresh
      await fetchData();
      setSelectedIds(new Set());
      setSelectMode(false);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to delete all images';
      setError(message);
    } finally {
      setBulkDeleting(false);
    }
  };

  const toggleSelect = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleSelectAll = () => {
    if (selectedIds.size === images.length) {
      // Deselect all
      setSelectedIds(new Set());
    } else {
      // Select all on current page
      setSelectedIds(new Set(images.map(img => img.id)));
    }
  };

  const triggerDownload = (blob: Blob, filename: string) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleDownloadSelected = async () => {
    const selected = images.filter(img => selectedIds.has(img.id));
    if (selected.length === 0) return;

    // Single image — direct download, no ZIP
    if (selected.length === 1) {
      const img = selected[0];
      try {
        setDownloading(true);
        setDownloadProgress({ current: 0, total: 1 });
        const resp = await fetch(img.r2Url);
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        let buf = await resp.arrayBuffer();
        // Embed prompt in PNG
        if ((img.format || 'png').toLowerCase() === 'png') {
          buf = embedPngTextChunk(buf, img.prompt);
        }
        setDownloadProgress({ current: 1, total: 1 });
        triggerDownload(new Blob([buf]), `${img.id}.${img.format || 'png'}`);
      } catch (err) {
        setError(`Download failed: ${err instanceof Error ? err.message : 'Unknown error'}`);
      } finally {
        setDownloading(false);
      }
      return;
    }

    // Multiple images — ZIP with metadata
    const abortController = new AbortController();
    downloadAbortRef.current = abortController;
    try {
      setDownloading(true);
      setDownloadProgress({ current: 0, total: selected.length });

      const fetched = await fetchImagesConcurrently(
        selected,
        5,
        (current) => setDownloadProgress({ current, total: selected.length }),
        abortController.signal,
      );

      if (abortController.signal.aborted) return;

      const zip = new JSZip();
      const manifest: Array<Record<string, unknown>> = [];

      for (const img of selected) {
        const buf = fetched.get(img.id);
        if (!buf) continue;

        const ext = img.format || 'png';
        const filename = `${img.id}.${ext}`;
        const finalBuf = ext.toLowerCase() === 'png'
          ? embedPngTextChunk(buf, img.prompt)
          : buf;
        zip.file(filename, finalBuf);

        manifest.push({
          id: img.id,
          filename,
          prompt: img.prompt,
          width: img.width,
          height: img.height,
          seed: null,
          createdAt: img.createdAt,
          deviceId: img.deviceId,
          sizeBytes: img.sizeBytes,
          format: ext,
          mediaType: img.mediaType,
        });
      }

      zip.file('manifest.json', JSON.stringify(manifest, null, 2));

      const blob = await zip.generateAsync({ type: 'blob' });
      triggerDownload(blob, `r2-gallery-${selected.length}-images.zip`);
    } catch (err) {
      if (!abortController.signal.aborted) {
        setError(`Download failed: ${err instanceof Error ? err.message : 'Unknown error'}`);
      }
    } finally {
      setDownloading(false);
      downloadAbortRef.current = null;
    }
  };

  const handleImageClick = (image: R2Image) => {
    if (selectMode) {
      setSelectedIds(prev => {
        const next = new Set(prev);
        if (next.has(image.id)) {
          next.delete(image.id);
        } else {
          next.add(image.id);
        }
        return next;
      });
    } else {
      setSelectedImage(image);
    }
  };

  const formatBytes = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const currentPage = Math.floor((filters.offset || 0) / (filters.limit || 24)) + 1;
  const totalPages = Math.ceil(total / (filters.limit || 24));
  const allSelected = images.length > 0 && selectedIds.size === images.length;

  if (loading && images.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex items-center gap-3 text-muted-foreground">
          <RefreshCw className="w-5 h-5 animate-spin" />
          <span>Loading gallery...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between animate-fade-in-up">
        <div>
          <h1 className="text-3xl font-bold text-gradient-violet">R2 Gallery</h1>
          <p className="text-muted-foreground mt-1">Generated images stored in Cloudflare R2</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setSelectMode(!selectMode)}
            className={cn(
              'px-4 py-2 rounded-xl flex items-center gap-2 text-sm transition-colors',
              selectMode
                ? 'bg-violet-500/20 text-violet-400 border border-violet-500/30'
                : 'btn-secondary'
            )}
          >
            {selectMode ? <XCircle className="w-4 h-4" /> : <CheckSquare className="w-4 h-4" />}
            {selectMode ? 'Cancel' : 'Select'}
          </button>
          <button
            onClick={fetchData}
            disabled={loading}
            className="btn-secondary px-4 py-2 rounded-xl flex items-center gap-2 text-sm"
          >
            <RefreshCw className={cn('w-4 h-4', loading && 'animate-spin')} />
            Refresh
          </button>
        </div>
      </div>

      {/* Selection Toolbar */}
      {selectMode && (
        <div className="flex items-center justify-between px-4 py-3 bg-violet-500/10 border border-violet-500/30 rounded-xl animate-fade-in-up">
          <div className="flex items-center gap-4">
            <button
              onClick={handleSelectAll}
              className="flex items-center gap-2 text-sm text-violet-400 hover:text-violet-300 transition-colors"
            >
              {allSelected ? (
                <CheckSquare className="w-4 h-4" />
              ) : (
                <Square className="w-4 h-4" />
              )}
              {allSelected ? 'Deselect All' : 'Select All'}
            </button>
            <span className="text-sm text-muted-foreground">
              {selectedIds.size} selected
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleDownloadSelected}
              disabled={selectedIds.size === 0 || downloading || bulkDeleting}
              className={cn(
                'px-4 py-2 rounded-xl text-sm flex items-center gap-2 transition-colors',
                selectedIds.size > 0 && !downloading
                  ? 'bg-cyan-500/20 text-cyan-400 hover:bg-cyan-500/30'
                  : 'bg-secondary text-muted-foreground cursor-not-allowed'
              )}
            >
              {downloading ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  {downloadProgress.current}/{downloadProgress.total}
                </>
              ) : (
                <>
                  <Download className="w-4 h-4" />
                  Download Selected
                </>
              )}
            </button>
            <button
              onClick={handleBulkDelete}
              disabled={selectedIds.size === 0 || bulkDeleting || downloading}
              className={cn(
                'px-4 py-2 rounded-xl text-sm flex items-center gap-2 transition-colors',
                selectedIds.size > 0
                  ? 'bg-rose-500/20 text-rose-400 hover:bg-rose-500/30'
                  : 'bg-secondary text-muted-foreground cursor-not-allowed'
              )}
            >
              {bulkDeleting ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                <Trash2 className="w-4 h-4" />
              )}
              Delete Selected
            </button>
            <button
              onClick={handleDeleteAll}
              disabled={bulkDeleting || total === 0}
              className="px-4 py-2 rounded-xl text-sm flex items-center gap-2 bg-rose-600/30 text-rose-300 hover:bg-rose-600/50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {bulkDeleting ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                <Trash2 className="w-4 h-4" />
              )}
              Delete All ({total})
            </button>
          </div>
        </div>
      )}

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 animate-fade-in-up">
          <div className="stat-card-violet p-5 rounded-2xl">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-violet-500/20 flex items-center justify-center">
                <Hash className="w-6 h-6 text-violet-400" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Media</p>
                <p className="text-2xl font-bold">{stats.totalImages.toLocaleString()}</p>
              </div>
            </div>
          </div>
          <div className="stat-card-emerald p-5 rounded-2xl">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                <HardDrive className="w-6 h-6 text-emerald-400" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Size</p>
                <p className="text-2xl font-bold">{stats.totalMB.toFixed(2)} MB</p>
              </div>
            </div>
          </div>
          <div className="stat-card-cyan p-5 rounded-2xl">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-cyan-500/20 flex items-center justify-center">
                <Image className="w-6 h-6 text-cyan-400" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Avg Size</p>
                <p className="text-2xl font-bold">
                  {stats.totalImages > 0
                    ? formatBytes(stats.totalBytes / stats.totalImages)
                    : '0 B'}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Error Alert */}
      {error && (
        <div className="bg-rose-500/10 border border-rose-500/30 rounded-xl p-5 animate-scale-in">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-rose-400" />
            <div className="flex-1">
              <p className="text-sm text-rose-300/80">{error}</p>
            </div>
            <button onClick={() => setError(null)} className="text-rose-400 hover:text-rose-300">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Search */}
      <div className="card-glow rounded-2xl p-4 animate-fade-in-up">
        <div className="flex gap-3">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search prompts..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              className="input-neural w-full pl-10 pr-4 py-2.5 rounded-xl text-sm"
            />
          </div>
          <button
            onClick={handleSearch}
            className="btn-glow-violet px-5 py-2.5 rounded-xl text-sm flex items-center gap-2"
          >
            <Search className="w-4 h-4" />
            Search
          </button>
          {filters.search && (
            <button
              onClick={() => {
                setSearchInput('');
                setFilters(prev => ({ ...prev, search: undefined, offset: 0 }));
              }}
              className="btn-secondary px-4 py-2.5 rounded-xl text-sm"
            >
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Image Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 animate-fade-in-up animate-delay-100">
        {images.map((image, index) => {
          const isSelected = selectedIds.has(image.id);
          return (
            <div
              key={image.id}
              className={cn(
                'group relative aspect-square rounded-xl overflow-hidden cursor-pointer card-glow animate-fade-in-up',
                selectMode && isSelected && 'ring-2 ring-violet-500 ring-offset-2 ring-offset-background'
              )}
              style={{ animationDelay: `${index * 30}ms` }}
              onClick={() => handleImageClick(image)}
            >
              {image.mediaType === 'video' ? (
                <div className="relative w-full h-full">
                  <video
                    src={image.r2Url}
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                    muted
                    loop
                    playsInline
                    preload="metadata"
                    onMouseEnter={(e) => (e.target as HTMLVideoElement).play().catch(() => {})}
                    onMouseLeave={(e) => { const v = e.target as HTMLVideoElement; v.pause(); v.currentTime = 0; }}
                  />
                  <div className="absolute top-2 left-2 px-1.5 py-0.5 rounded bg-black/60 text-white text-xs flex items-center gap-1 pointer-events-none">
                    <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor"><polygon points="5,3 19,12 5,21" /></svg>
                    Video
                  </div>
                </div>
              ) : (
                <img
                  src={image.thumbnailUrl || image.r2Url}
                  alt={image.prompt.slice(0, 50)}
                  className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                  loading="lazy"
                />
              )}
              {/* Overlay */}
              <div className={cn(
                'absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent transition-opacity duration-200',
                selectMode ? 'opacity-50' : 'opacity-0 group-hover:opacity-100'
              )}>
                <div className="absolute bottom-0 left-0 right-0 p-3">
                  <p className="text-xs text-white/90 line-clamp-2">{image.prompt}</p>
                </div>
              </div>

              {/* Selection checkbox */}
              {selectMode && (
                <button
                  onClick={(e) => toggleSelect(image.id, e)}
                  className={cn(
                    'absolute top-2 left-2 p-1 rounded-md transition-all duration-200',
                    isSelected
                      ? 'bg-violet-500 text-white'
                      : 'bg-black/50 text-white/80 hover:bg-black/70'
                  )}
                >
                  {isSelected ? (
                    <CheckSquare className="w-5 h-5" />
                  ) : (
                    <Square className="w-5 h-5" />
                  )}
                </button>
              )}

              {/* Delete button (only show when not in select mode) */}
              {!selectMode && (
                <button
                  onClick={(e) => handleDelete(image.id, e)}
                  disabled={deletingId === image.id}
                  className="absolute top-2 right-2 p-1.5 rounded-lg bg-black/50 text-white/80 hover:text-rose-400 hover:bg-black/70 opacity-0 group-hover:opacity-100 transition-all duration-200"
                >
                  {deletingId === image.id ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <Trash2 className="w-4 h-4" />
                  )}
                </button>
              )}
            </div>
          );
        })}
      </div>

      {/* Empty State */}
      {images.length === 0 && !loading && (
        <div className="flex flex-col items-center justify-center py-16">
          <div className="w-20 h-20 rounded-2xl bg-secondary flex items-center justify-center mb-4">
            <Image className="w-10 h-10 text-muted-foreground" />
          </div>
          <p className="text-muted-foreground mb-1">No images found</p>
          <p className="text-sm text-muted-foreground/70">
            {filters.search ? 'Try a different search term' : 'Generated images will appear here'}
          </p>
        </div>
      )}

      {/* Pagination */}
      {total > 0 && (
        <div className="flex items-center justify-between px-4 py-4 card-glow rounded-2xl">
          <div className="text-sm text-muted-foreground">
            Showing {(filters.offset || 0) + 1} - {Math.min((filters.offset || 0) + (filters.limit || 24), total)} of {total}
          </div>
          <div className="flex items-center gap-4">
            {/* Page size selector */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Per page:</span>
              <select
                value={filters.limit || 24}
                onChange={(e) => setFilters(prev => ({ ...prev, limit: Number(e.target.value), offset: 0 }))}
                className="input-neural px-3 py-1.5 rounded-lg text-sm bg-secondary border-none"
              >
                <option value={24}>24</option>
                <option value={48}>48</option>
                <option value={96}>96</option>
                <option value={150}>150</option>
                <option value={300}>300</option>
              </select>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => handlePageChange('prev')}
                disabled={currentPage === 1}
                className="p-2 rounded-lg bg-secondary text-muted-foreground hover:text-foreground disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-sm text-muted-foreground">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => handlePageChange('next')}
                disabled={currentPage === totalPages}
                className="p-2 rounded-lg bg-secondary text-muted-foreground hover:text-foreground disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Image Detail Modal */}
      {selectedImage && (
        <ImageDetailModal
          image={selectedImage}
          onClose={() => setSelectedImage(null)}
          onDelete={() => handleDelete(selectedImage.id)}
          formatBytes={formatBytes}
        />
      )}
    </div>
  );
}

function ImageDetailModal({
  image,
  onClose,
  onDelete,
  formatBytes,
}: {
  image: R2Image;
  onClose: () => void;
  onDelete: () => void;
  formatBytes: (bytes: number) => string;
}) {
  return (
    <div
      className="fixed inset-0 modal-overlay flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="modal-content rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden animate-scale-in"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Image */}
        <div className="relative bg-black/50 flex items-center justify-center">
          {image.mediaType === 'video' ? (
            <video
              src={image.r2Url}
              controls
              autoPlay
              loop
              muted
              playsInline
              className="max-w-full max-h-[60vh] object-contain"
            />
          ) : (
            <img
              src={image.r2Url}
              alt={image.prompt}
              className="max-w-full max-h-[60vh] object-contain"
            />
          )}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 rounded-lg bg-black/50 text-white hover:bg-black/70 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Details */}
        <div className="p-6 space-y-4">
          {/* Prompt */}
          <div>
            <p className="text-xs text-muted-foreground mb-1">Prompt</p>
            <p className="text-sm">{image.prompt}</p>
          </div>

          {/* Metadata Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="p-3 rounded-xl bg-secondary/30">
              <p className="text-xs text-muted-foreground mb-1">Size</p>
              <p className="font-mono text-sm">{formatBytes(image.sizeBytes)}</p>
            </div>
            <div className="p-3 rounded-xl bg-secondary/30">
              <p className="text-xs text-muted-foreground mb-1">Format</p>
              <p className="font-mono text-sm uppercase">{image.format || 'png'}</p>
            </div>
            {image.width && image.height && (
              <div className="p-3 rounded-xl bg-secondary/30">
                <p className="text-xs text-muted-foreground mb-1">Dimensions</p>
                <p className="font-mono text-sm">{image.width} x {image.height}</p>
              </div>
            )}
            <div className="p-3 rounded-xl bg-secondary/30">
              <p className="text-xs text-muted-foreground mb-1">Created</p>
              <p className="text-sm">{formatDateTime(new Date(image.createdAt).getTime())}</p>
            </div>
            {image.deviceId && (
              <div className="p-3 rounded-xl bg-secondary/30">
                <p className="text-xs text-muted-foreground mb-1">Device</p>
                <p className="font-mono text-xs truncate" title={image.deviceId}>
                  {image.deviceId.slice(0, 12)}...
                </p>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between pt-4 border-t border-border">
            <div className="flex items-center gap-2">
              <a
                href={image.r2Url}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-secondary px-4 py-2 rounded-xl text-sm flex items-center gap-2"
              >
                <ExternalLink className="w-4 h-4" />
                Open
              </a>
              <a
                href={image.r2Url}
                download={`${image.id}.${image.format || 'png'}`}
                className="btn-secondary px-4 py-2 rounded-xl text-sm flex items-center gap-2"
              >
                <Download className="w-4 h-4" />
                Download
              </a>
            </div>
            <button
              onClick={onDelete}
              className="px-4 py-2 rounded-xl text-sm flex items-center gap-2 bg-rose-500/10 text-rose-400 hover:bg-rose-500/20 transition-colors"
            >
              <Trash2 className="w-4 h-4" />
              Delete
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
